/**
 * Sequence Diagram Data Model
 * Phase 2: AST Parsing for Repository Analysis
 *
 * Represents sequence diagrams for Authentication Flow and Request Lifecycle.
 * Shows interactions between participants over time.
 */
import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
/**
 * Type of participant in a sequence diagram
 */
export const ParticipantType = z.enum([
    'user', // End user
    'frontend', // Frontend application
    'api', // API/Backend
    'service', // Internal service
    'database', // Database
    'cache', // Cache layer
    'queue', // Message queue
    'external', // External service/API
    'middleware', // Middleware component
    'auth', // Authentication service
]);
/**
 * A participant in the sequence diagram
 */
export const SequenceParticipantSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Display name */
    name: z.string(),
    /** Type of participant */
    type: ParticipantType,
    /** Technology/framework */
    technology: z.string().optional(),
    /** Description */
    description: z.string().optional(),
    /** Order in the diagram (left to right) */
    order: z.number(),
    /** Code evidence supporting this participant (Issue #203) - DEPRECATED: use data.evidence instead */
    evidence: z.array(CodeEvidenceSchema).optional(),
    /** Arbitrary data for this participant including evidence (Issue #223) */
    data: z.record(z.unknown()).optional(),
});
/**
 * Type of message/interaction
 */
export const MessageType = z.enum([
    'sync', // Synchronous call (solid arrow)
    'async', // Asynchronous call (dashed arrow)
    'return', // Return message (dashed arrow, opposite direction)
    'self', // Self-call (loop back)
    'create', // Object creation
    'destroy', // Object destruction
]);
/**
 * A message/interaction in the sequence
 */
export const SequenceMessageSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Source participant */
    from: z.string(),
    /** Target participant */
    to: z.string(),
    /** Message type */
    type: MessageType,
    /** Message label/description */
    label: z.string(),
    /** Optional note */
    note: z.string().optional(),
    /** Sequence number (for ordering) */
    sequence: z.number(),
    /** Whether this message is in an alternative block */
    altBlockId: z.string().optional(),
    /** Whether this message is in a loop block */
    loopBlockId: z.string().optional(),
    /** Whether this message is in an optional block */
    optBlockId: z.string().optional(),
    /** HTTP method if applicable */
    httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']).optional(),
    /** Response status if this is a return message */
    responseStatus: z.number().optional(),
});
/**
 * An alternative block (if/else branching)
 */
export const AlternativeBlockSchema = z.object({
    /** Block identifier */
    id: z.string(),
    /** Condition for the main branch */
    condition: z.string(),
    /** Label for the main branch */
    mainLabel: z.string(),
    /** Alternative conditions and labels */
    alternatives: z.array(z.object({
        condition: z.string(),
        label: z.string(),
    })).default([]),
    /** Starting sequence number */
    startSequence: z.number(),
    /** Ending sequence number */
    endSequence: z.number(),
});
/**
 * A loop block
 */
export const LoopBlockSchema = z.object({
    /** Block identifier */
    id: z.string(),
    /** Loop condition */
    condition: z.string(),
    /** Starting sequence number */
    startSequence: z.number(),
    /** Ending sequence number */
    endSequence: z.number(),
});
/**
 * A note on the sequence diagram
 */
export const SequenceNoteSchema = z.object({
    /** Note identifier */
    id: z.string(),
    /** Participant(s) the note is attached to */
    over: z.array(z.string()),
    /** Note text */
    text: z.string(),
    /** Position relative to sequence */
    afterSequence: z.number(),
    /** Position: left, right, or over */
    position: z.enum(['left', 'right', 'over']).default('over'),
});
/**
 * Type of sequence diagram
 */
export const SequenceDiagramType = z.enum([
    'generic', // Generic sequence
]);
/**
 * Complete Sequence Diagram data
 */
export const SequenceDiagramDataSchema = z.object({
    /** Diagram metadata */
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        diagramType: z.literal('sequenceDiagram'),
        sequenceType: SequenceDiagramType,
        c4Level: z.literal('component').default('component'),
        generatedAt: z.date().optional(),
        /** Source files analyzed (Issue #177) */
        sourceFiles: z.array(z.string()).optional(),
        /** Code evidence showing where patterns were detected (Issue #177) */
        sourceEvidence: z.array(CodeEvidenceSchema).optional(),
    }),
    /** Participants in order */
    participants: z.array(SequenceParticipantSchema),
    /** Messages/interactions in sequence order */
    messages: z.array(SequenceMessageSchema),
    /** Alternative (if/else) blocks */
    altBlocks: z.array(AlternativeBlockSchema).default([]),
    /** Loop blocks */
    loopBlocks: z.array(LoopBlockSchema).default([]),
    /** Notes */
    notes: z.array(SequenceNoteSchema).default([]),
    /** For auth flows: authentication method used */
    authMethod: z.enum(['jwt', 'session', 'oauth', 'apiKey', 'basic', 'custom']).optional(),
    /** For request lifecycle: the endpoint being traced */
    endpoint: z.string().optional(),
    /** For request lifecycle: middleware chain */
    middlewareChain: z.array(z.string()).default([]),
    /** Cache points identified */
    cachePoints: z.array(z.object({
        participantId: z.string(),
        afterSequence: z.number(),
        description: z.string(),
    })).default([]),
});
/**
 * Create an empty Sequence diagram
 */
export function createSequenceDiagram(title, sequenceType, options) {
    return {
        metadata: {
            title,
            diagramType: 'sequenceDiagram',
            sequenceType,
            c4Level: 'component',
            ...options,
        },
        participants: [],
        messages: [],
        altBlocks: [],
        loopBlocks: [],
        notes: [],
        middlewareChain: [],
        cachePoints: [],
    };
}
/**
 * Participant styling
 */
export const PARTICIPANT_STYLES = {
    user: { bg: '#e3f2fd', border: '#1565c0', icon: '👤' },
    frontend: { bg: '#e8f5e9', border: '#2e7d32', icon: '🖥️' },
    api: { bg: '#fff3e0', border: '#ef6c00', icon: '🔌' },
    service: { bg: '#f3e5f5', border: '#7b1fa2', icon: '⚙️' },
    database: { bg: '#fff8e1', border: '#f9a825', icon: '💾' },
    cache: { bg: '#e0f7fa', border: '#00838f', icon: '⚡' },
    queue: { bg: '#fce4ec', border: '#c2185b', icon: '📬' },
    external: { bg: '#eceff1', border: '#546e7a', icon: '🌐' },
    middleware: { bg: '#f1f8e9', border: '#689f38', icon: '🔗' },
    auth: { bg: '#ffebee', border: '#c62828', icon: '🔐' },
};
//# sourceMappingURL=sequence.js.map